<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(tKey + '.general.title')"
            :description="$t(tKey + '.general.description')"
          >
            <div class="flex flex-col gap-y-5">
              <FormInput
                v-model="rawInterestZone.name"
                :error="errors?.name"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('interestZone.name')"
                :placeholder="$t('interestZone.name')"
              />
              <Multiselect
                v-model="rawInterestZone.region"
                :error="errors?.region"
                :options="regions"
                :label="$t('mounting.region')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
                @select="setMapCenter()"
              />
              <LocationMap ref="map" v-model="rawInterestZone.gisLocation" v-model:radius="rawInterestZone.radius" />
              <div class="flex gap-x-5">
                <FormInput
                  :model-value="rawInterestZone.gisLocation?.lat"
                  :error="errors?.gisLocation?.lat"
                  class="w-1/2"
                  type="text"
                  :label="$t('lat')"
                  pattern="^\d*\.?\d*$"
                  @update:model-value="setCoords($event, rawInterestZone.gisLocation?.long)"
                />
                <FormInput
                  :model-value="rawInterestZone.gisLocation?.long"
                  :error="errors?.gisLocation?.long"
                  class="w-1/2"
                  type="text"
                  :label="$t('long')"
                  pattern="^\d*\.?\d*$"
                  @update:model-value="setCoords(rawInterestZone.gisLocation?.lat, $event)"
                />
              </div>
              <FormInput
                v-model="rawInterestZone.radius"
                :error="errors?.radius"
                class="col-start-1 col-end-2"
                type="number"
                :label="$t('interestZone.radius')"
                :placeholder="$t('interestZone.radius')"
              />
              <div class="flex gap-x-5 mb-5">
                <DatePicker
                  v-model="rawInterestZone.validityStart"
                  :error="errors?.validityStart"
                  mode="datetime"
                  class="w-full"
                  :label="$t('interestZone.validityStart')"
                />
                <DatePicker
                  v-model="rawInterestZone.validityEnd"
                  :error="errors?.validityEnd"
                  mode="datetime"
                  class="w-full"
                  :label="$t('interestZone.validityEnd')"
                />
              </div>
              <MultiSwitch :model-value="rawInterestZone.type" :options="interestZoneTypeOptions" @update:model-value="(event) => {rawInterestZone.threshold = undefined; rawInterestZone.type = event; }" />
              <InputDuration
                v-if="rawInterestZone.type === 'time'"
                v-model="rawInterestZone.threshold"
                :error="errors?.threshold"
                class="col-start-1 col-end-2"
                type="number"
                :label="$t('interestZone.perTime')"
                placeholder="hh:mm:ss"
              />
              <FormInput
                  v-else
                  v-model="rawInterestZone.threshold"
                  :error="errors?.threshold"
                  class="col-start-1 col-end-2"
                  type="number"
                  :label="$t('interestZone.perPoint')"
              />
              <FormInput
                v-model="rawInterestZone.multiplier"
                :error="errors?.multiplier"
                class="col-start-1 col-end-2"
                type="number"
                :label="rawInterestZone.type === 'distance' ? $t('interestZone.mult') : $t('interestZone.num')"
                :placeholder="rawInterestZone.type === 'distance' ? $t('interestZone.mult') : $t('interestZone.num')"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {computed, ref} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {date, number, object, string} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import type { InterestZone, Region } from '@/entities/Entities';
import MultiSwitch from '@utils/inputs/MultiSwitch.vue';
import InputDuration from '@utils/inputs/InputDuration.vue';

interface Props {
  open: boolean;
  interestZone?: InterestZone;
}

const emit = defineEmits(['update:open', 'update:interestZone', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalInterestZone = useVModel(props, 'interestZone', emit);

const isNew = computed(() => !props.interestZone);
const tKey = computed(() =>
  (isNew.value ? 'pages.interestZoneList.modal.new' : 'pages.interestZoneList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const interestZoneTypeOptions = [
  {
    value: 'distance',
    name: t('interestZone.type.distance'),
  },
  {
    value: 'time',
    name: t('interestZone.type.time'),
  },
];

const initialData = (interestZone?: InterestZone) => (interestZone
  ? { ...interestZone }
  : {
    name: undefined,
    gisLocation: undefined,
    region: undefined,
    radius: undefined,
    multiplier: undefined,
    type: 'distance',
    threshold: undefined,
    validityStart: undefined,
    validityEnd: undefined,
  });

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);
const regions = ref<{ label: string, value: string|null, gisLocation?: { lat: number, long: number } }[]>([]);
const loadRegions = async () => {
  regions.value = await fetchRegions();
};
loadRegions();

const rawInterestZone = syncRef(internalInterestZone, (interestZone) => ({
  ...initialData(interestZone),
  region: interestZone?.region?.['@id'],
}));

const {
  data: interestZone,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    name: string().nullable().required(t('validation.notEmpty')),
    region: string().nullable().required(t('validation.notEmpty')),
    gisLocation: object({
      lat: number().typeError(t('validation.number')).nullable().required(t('validation.notEmpty')),
      long: number().typeError(t('validation.number')).nullable().required(t('validation.notEmpty')),
    }),
    radius: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .nullable()
      .required(t('validation.notEmpty')),
    multiplier: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .nullable()
      .required(t('validation.notEmpty')),
    type: string(),
    threshold: number()
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .typeError(t('validation.number'))
      .required(t('validation.notEmpty')),
    validityStart: date().nullable(),
    validityEnd: date().nullable(),
  }),
  rawInterestZone,
);

function resetData() {
  rawInterestZone.value = { ...initialData() } as unknown as typeof interestZone['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalInterestZone.value = undefined;
}

const map = ref();

const setMapCenter = () => {
  const gisLocation = regions.value.find((region) => region.value === rawInterestZone.value?.region)?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const setCoords = (lat: number|undefined, long: number|undefined) => {
  rawInterestZone.value.gisLocation = {
    lat: lat || 0,
    long: long || 0,
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.interestZone;
    const url = edit ? props.interestZone['@id'] : '/interest_areas';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: interestZone.value.name,
        region: interestZone.value.region,
        gisLocation: interestZone.value.gisLocation,
        radius: interestZone.value.radius,
        multiplier: interestZone.value.multiplier,
        type: interestZone.value.type,
        threshold: interestZone.value.threshold,
        validityStart: interestZone.value.validityStart,
        validityEnd: interestZone.value.validityEnd,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
